import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import "./DocumentRecords.css";
import API_BASE_URL from "../config";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import {
  FiFile,
  FiUpload,
  FiDownload,
  FiEdit2,
  FiTrash2,
  FiCheck,
  FiX,
  FiCamera,
  FiSmartphone,
  FiMonitor,
  FiCalendar,
  FiUser,
  FiAlertCircle,
  FiLoader,
  FiEye,
  FiSave,
  FiChevronRight,
  FiPackage,
  FiShare2,
  FiLink,
  FiClock,
  FiPrinter,
  FiRefreshCw
} from "react-icons/fi";

const DocumentRecords = ({ employee }) => {
  const [documents, setDocuments] = useState([]);
  const [file, setFile] = useState(null);
  const [docName, setDocName] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [showQRCode, setShowQRCode] = useState(false);
  const [uploadSession, setUploadSession] = useState("");
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [uploadMethod, setUploadMethod] = useState("desktop");
  const [mobileUploadStatus, setMobileUploadStatus] = useState("waiting");
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const documentTypes = [
    "10th Marksheet",
    "12th Marksheet",
    "UG Marksheet",
    "PG Marksheet",
    "Aadhaar Card",
    "PAN Card",
    "Bank Passbook",
    "Passport",
    "Passport Size Photo",
    "Experience Letter",
    "Resume",
    "Offer Letter",
    "Appointment Letter",
    "Salary Slip",
    "Form 16",
  ];

  const documentIcons = {
    "10th Marksheet": "📘",
    "12th Marksheet": "📗",
    "UG Marksheet": "🎓",
    "PG Marksheet": "📚",
    "Aadhaar Card": "🆔",
    "PAN Card": "💳",
    "Bank Passbook": "🏦",
    "Passport": "📒",
    "Passport Size Photo": "📸",
    "Experience Letter": "📝",
    "Resume": "📄",
    "Offer Letter": "📃",
    "Appointment Letter": "📑",
    "Salary Slip": "💰",
    "Form 16": "🧾",
  };

  // Check if device is mobile
  const isMobileDevice = useCallback(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }, []);

  // Generate unique upload session
  const generateUploadSession = useCallback(() => {
    const sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
    setUploadSession(sessionId);
    return sessionId;
  }, []);

  // Create mobile upload URL
  const getMobileUploadUrl = useCallback(() => {
    const sessionId = generateUploadSession();
    const uploadData = {
      sessionId: sessionId,
      employeeId: employee?._id,
      emp_id: employee?.emp_id,
      documentType: docName,
      timestamp: Date.now(),
      baseUrl: window.location.origin
    };
    
    const url = `${window.location.origin}/mobile-upload?data=${encodeURIComponent(JSON.stringify(uploadData))}`;
    setQrCodeUrl(url);
    setMobileUploadStatus("waiting");
    return url;
  }, [employee?._id, employee?.emp_id, docName, generateUploadSession]);

  // Poll for uploaded file
  const checkMobileUpload = useCallback(async (sessionId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/documents/mobile-upload/${sessionId}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );
      
      if (response.data.success && response.data.uploaded) {
        // File uploaded from mobile
        setShowQRCode(false);
        setMobileUploadStatus("uploaded");
        toast.success("File uploaded successfully from mobile! ✅");
        
        // Refresh documents list
        fetchDocuments();
        return true;
      }
    } catch (error) {
      console.error("❌ Error checking mobile upload:", error);
    }
    return false;
  }, []);

  // Start polling when QR code is shown
  useEffect(() => {
    let pollInterval;
    if (showQRCode && uploadSession) {
      pollInterval = setInterval(() => {
        checkMobileUpload(uploadSession);
      }, 2000); // Check every 2 seconds
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [showQRCode, uploadSession, checkMobileUpload]);

  // Fetch documents using emp_id
  const fetchDocuments = useCallback(async () => {
    if (!employee || !employee.emp_id) {
      console.log("❌ No employee or emp_id found");
      return;
    }
    
    try {
      console.log(`🔍 Fetching documents for emp_id: ${employee.emp_id}`);
      
      const response = await axios.get(
        `${API_BASE_URL}/api/documents/by-emp-id/${employee.emp_id}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );
      
      console.log("Documents API response:", response.data);
      
      if (response.data.success) {
        setDocuments(response.data.data);
        console.log(`✅ Loaded ${response.data.data.length} documents`);
      } else {
        setDocuments([]);
        toast.error(response.data.message || "Failed to fetch documents");
      }
    } catch (err) {
      console.error("❌ Error fetching documents:", err);
      setDocuments([]);
      toast.error(err.response?.data?.error || "Failed to fetch documents");
    }
  }, [employee]);

  useEffect(() => {
    if (employee && employee.emp_id) {
      fetchDocuments();
    }
  }, [employee, fetchDocuments]);

  const handleFileSelect = () => {
    if (isMobileDevice()) {
      // For mobile devices, always use direct file selection
      fileInputRef.current.setAttribute('capture', 'environment');
      fileInputRef.current.setAttribute('accept', 'image/*,application/pdf');
      fileInputRef.current.click();
    } else {
      // For desktop, based on selected method
      if (uploadMethod === "desktop") {
        // Direct file upload
        fileInputRef.current.removeAttribute('capture');
        fileInputRef.current.setAttribute('accept', 'image/*,application/pdf');
        fileInputRef.current.click();
      } else {
        // Mobile scan method
        if (!docName) {
          toast.error("Please select a document type first");
          return;
        }
        if (!employee?.emp_id) {
          toast.error("Please select an employee first");
          return;
        }
        getMobileUploadUrl();
        setShowQRCode(true);
      }
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file size (5MB limit)
      if (selectedFile.size > 5 * 1024 * 1024) {
        toast.error("File size exceeds 5MB limit");
        e.target.value = ""; // Clear the input
        return;
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error("Only JPEG, PNG, and PDF files are allowed");
        e.target.value = ""; // Clear the input
        return;
      }
      
      setFile(selectedFile);
      toast.success(`Selected: ${selectedFile.name}`);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!employee || !employee.emp_id) {
      toast.error("Employee information not found");
      return;
    }
    
    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }
    
    if (!docName) {
      toast.error("Please select a document type");
      return;
    }

    const formData = new FormData();
    formData.append("employeeId", employee._id || "");
    formData.append("emp_id", employee.emp_id);
    formData.append("document", file);
    formData.append("name", docName);

    try {
      setLoading(true);
      setUploadProgress(0);
      
      const response = await axios.post(
        `${API_BASE_URL}/api/documents/upload`,
        formData,
        {
          headers: { 
            "Content-Type": "multipart/form-data",
            "Authorization": `Bearer ${sessionStorage.getItem('token')}`
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      if (response.data.success) {
        setFile(null);
        setDocName("");
        setUploadProgress(0);
        
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        
        fetchDocuments();
        toast.success("Document uploaded successfully! ✅");
      } else {
        toast.error(response.data.error || "Upload failed");
      }
    } catch (err) {
      console.error("❌ Upload error:", err);
      toast.error(err.response?.data?.error || "Failed to upload document");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (docId) => {
    if (!editingName.trim()) {
      toast.error("Enter a new name");
      return;
    }
    
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/documents/${docId}`,
        { name: editingName.trim() },
        {
          headers: {
            "Authorization": `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );
      
      if (response.data.success) {
        setEditingId(null);
        setEditingName("");
        fetchDocuments();
        toast.success("Document updated successfully! ✅");
      } else {
        toast.error(response.data.error || "Update failed");
      }
    } catch (err) {
      console.error("❌ Edit error:", err);
      toast.error(err.response?.data?.error || "Failed to update document");
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/documents/${docId}`,
        {
          headers: {
            "Authorization": `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );
      
      if (response.data.success) {
        fetchDocuments();
        toast.success("Document deleted successfully! ✅");
      } else {
        toast.error(response.data.error || "Delete failed");
      }
    } catch (err) {
      console.error("❌ Delete error:", err);
      toast.error(err.response?.data?.error || "Failed to delete document");
    }
  };

  const handleViewDocument = (doc) => {
    if (doc.file) {
      window.open(doc.file, '_blank');
    } else if (doc._id) {
      window.open(`${API_BASE_URL}/api/documents/file/${doc._id}`, '_blank');
    }
  };

  const getFileInputButtonText = () => {
    if (file) {
      return `✅ ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)`;
    }

    if (isMobileDevice()) {
      return "Open Camera/File Picker";
    }

    if (uploadMethod === "desktop") {
      return "Choose File from Computer";
    } else {
      return "Generate QR Code for Mobile";
    }
  };

  const getStatusMessage = () => {
    switch (mobileUploadStatus) {
      case "waiting":
        return "Waiting for mobile upload...";
      case "uploading":
        return "Uploading from mobile...";
      case "uploaded":
        return "Upload completed!";
      default:
        return "Waiting for mobile upload...";
    }
  };

  return (
    <div className="document-records-container">
      <div className="document-records-header">
        <h2><FiFile style={{ marginRight: '10px', verticalAlign: 'middle' }} /> Employee Document Management</h2>
        <p>
          <FiUser style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Manage documents for: {employee?.name || "N/A"} (ID: {employee?.emp_id || "N/A"})
        </p>
      </div>

      <div className="document-records-content">
        <div className="upload-section">
          <div className="upload-info">
            <h3><FiUpload style={{ marginRight: '10px', verticalAlign: 'middle' }} /> Upload New Document</h3>
            <p>Supported formats: PDF, JPG, PNG (Max size: 5MB)</p>
            <p className="device-info">
              {isMobileDevice() ? 
                <><FiSmartphone style={{ marginRight: '8px', verticalAlign: 'middle' }} /> Mobile: Camera will open directly</> : 
                <><FiMonitor style={{ marginRight: '8px', verticalAlign: 'middle' }} /> Desktop: Choose your preferred upload method</>
              }
            </p>
          </div>
          
          <form onSubmit={handleUpload} className="upload-form">
            <div className="form-group">
              <label>Document Type *</label>
              <select
                value={docName}
                onChange={(e) => setDocName(e.target.value)}
                required
                disabled={loading}
              >
                <option value="">Select Document Type</option>
                {documentTypes.map((type, idx) => (
                  <option key={idx} value={type}>
                    {documentIcons[type]} {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Upload Method Selection - Only show on desktop */}
            {!isMobileDevice() && (
              <div className="form-group">
                <label>Upload Method</label>
                <div className="upload-method-options">
                  <label className="method-option">
                    <input
                      type="radio"
                      name="uploadMethod"
                      value="desktop"
                      checked={uploadMethod === "desktop"}
                      onChange={(e) => setUploadMethod(e.target.value)}
                      disabled={loading}
                    />
                    <span className="method-content">
                      <span className="method-icon"><FiMonitor /></span>
                      <span className="method-text">
                        <strong>Direct Upload</strong>
                        <small>Upload directly from your computer</small>
                      </span>
                    </span>
                  </label>
                  
                  <label className="method-option">
                    <input
                      type="radio"
                      name="uploadMethod"
                      value="mobile"
                      checked={uploadMethod === "mobile"}
                      onChange={(e) => setUploadMethod(e.target.value)}
                      disabled={loading}
                    />
                    <span className="method-content">
                      <span className="method-icon"><FiSmartphone /></span>
                      <span className="method-text">
                        <strong>Mobile Scan</strong>
                        <small>Scan QR code to upload from phone</small>
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            )}

            <div className="form-group">
              <label>
                {uploadMethod === "desktop" ? "Select File *" : "Generate QR Code"}
              </label>
              <div className="file-input-container">
                <input
                  ref={fileInputRef}
                  type="file"
                  id="fileInput"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  accept="image/*,application/pdf"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={handleFileSelect}
                  className="file-input-button"
                  disabled={loading || (uploadMethod === "mobile" && !docName)}
                >
                  <span className="file-placeholder">
                    {uploadMethod === "desktop" ? (
                      <><FiFile style={{ marginRight: '8px' }} /> {getFileInputButtonText()}</>
                    ) : (
                      <><FiShare2 style={{ marginRight: '8px' }} /> {getFileInputButtonText()}</>
                    )}
                  </span>
                </button>
              </div>
              {!isMobileDevice() && uploadMethod === "mobile" && (
                <p className="file-input-hint">
                  <FiChevronRight style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  Click to generate QR code that can be scanned with your mobile device
                </p>
              )}
              {!isMobileDevice() && uploadMethod === "desktop" && (
                <p className="file-input-hint">
                  <FiChevronRight style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  Click to choose a file from your computer (Max 5MB)
                </p>
              )}
            </div>

            {uploadMethod === "desktop" && file && (
              <div className="file-preview">
                <span className="file-preview-icon"><FiFile /></span>
                <span className="file-preview-name">{file.name}</span>
                <span className="file-preview-size">
                  ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                </span>
              </div>
            )}

            {loading && uploadMethod === "desktop" && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <div className="progress-text">
                  <FiRefreshCw style={{ 
                    marginRight: '8px', 
                    verticalAlign: 'middle',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Uploading: {uploadProgress}%
                </div>
              </div>
            )}

            <button 
              type="submit" 
              className="upload-button" 
              disabled={loading || 
                (uploadMethod === "desktop" && !file) || 
                (uploadMethod === "mobile" && showQRCode) ||
                !docName ||
                !employee?.emp_id}
            >
              {loading ? (
                <>
                  <FiLoader style={{ 
                    marginRight: '8px', 
                    verticalAlign: 'middle',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Uploading... {uploadProgress}%
                </>
              ) : (
                <>
                  <FiUpload style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  {uploadMethod === "mobile" && showQRCode ? "Waiting for Mobile Upload" : "Upload Document"}
                </>
              )}
            </button>
          </form>
        </div>

        {/* QR Code Modal for Desktop */}
        {showQRCode && (
          <div className="qr-modal-overlay">
            <div className="qr-modal">
              <div className="qr-modal-header">
                <h3><FiSmartphone style={{ marginRight: '10px', verticalAlign: 'middle' }} /> Upload from Mobile</h3>
                <p>Document: {docName}</p>
                <button 
                  onClick={() => setShowQRCode(false)}
                  className="close-button"
                >
                  <FiX />
                </button>
              </div>
              <div className="qr-code-container">
                <div className="qr-code-wrapper">
                  <QRCodeSVG 
                    value={qrCodeUrl} 
                    size={256}
                    level="M"
                    includeMargin={true}
                  />
                </div>
                <div className="qr-instructions">
                  <h4>How to upload from mobile:</h4>
                  <ol>
                    <li><FiCamera style={{ marginRight: '8px' }} /> Open your mobile camera app</li>
                    <li>Scan this QR code</li>
                    <li>It will open an upload page on your mobile</li>
                    <li>Select or capture the document</li>
                    <li>File will be automatically uploaded</li>
                  </ol>
                  <div className="upload-status">
                    <div className={`status-indicator ${mobileUploadStatus}`}>
                      <div className="pulse-dot"></div>
                      <FiClock style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                      {getStatusMessage()}
                    </div>
                  </div>
                </div>
                
                <div className="direct-link">
                  <p><FiLink style={{ marginRight: '8px', verticalAlign: 'middle' }} /> Or open this link on your mobile:</p>
                  <input 
                    type="text" 
                    value={qrCodeUrl} 
                    readOnly 
                    className="url-input"
                    onClick={(e) => e.target.select()}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="documents-list-section">
          <div className="section-header">
            <h3><FiFile style={{ marginRight: '10px', verticalAlign: 'middle' }} /> Uploaded Documents</h3>
            <span className="doc-count">
              <FiPackage style={{ marginRight: '6px', verticalAlign: 'middle' }} />
              {documents.length} document{documents.length !== 1 ? 's' : ''}
            </span>
          </div>

          {documents.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><FiFile /></div>
              <h4>No documents uploaded yet</h4>
              <p>Upload your first document using the form above</p>
            </div>
          ) : (
            <div className="documents-grid">
              {documents.map((doc) => (
                <div key={doc._id} className="document-card">
                  <div className="document-card-header">
                    <span className="document-icon">
                      {documentIcons[doc.name] || <FiFile />}
                    </span>
                    {editingId === doc._id ? (
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="edit-input"
                        autoFocus
                        placeholder="Enter new name"
                      />
                    ) : (
                      <h4 className="document-name" title={doc.name}>
                        {doc.name}
                      </h4>
                    )}
                  </div>
                  
                  <div className="document-details">
                    <p className="upload-date" title="Upload date">
                      <FiCalendar style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                      {new Date(doc.uploadedAt).toLocaleDateString()} {new Date(doc.uploadedAt).toLocaleTimeString()}
                    </p>
                    <p className="upload-method">
                      {doc.uploadedVia === 'mobile' ? 
                        <><FiSmartphone style={{ marginRight: '6px', verticalAlign: 'middle' }} /> Mobile Upload</> : 
                        <><FiMonitor style={{ marginRight: '6px', verticalAlign: 'middle' }} /> Desktop Upload</>
                      }
                    </p>
                    {doc.size && (
                      <p className="file-size">
                        <FiPackage style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                        {(doc.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    )}
                  </div>
                  
                  <div className="document-actions">
                    <button
                      onClick={() => handleViewDocument(doc)}
                      className="view-button"
                      title="View document"
                    >
                      <FiEye style={{ marginRight: '8px' }} /> View Document
                    </button>
                    
                    <div className="action-buttons">
                      {editingId === doc._id ? (
                        <>
                          <button 
                            onClick={() => handleEdit(doc._id)}
                            className="save-button"
                            title="Save changes"
                          >
                            <FiSave style={{ marginRight: '8px' }} /> Save
                          </button>
                          <button 
                            onClick={() => setEditingId(null)}
                            className="cancel-button"
                            title="Cancel editing"
                          >
                            <FiX style={{ marginRight: '8px' }} /> Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              setEditingId(doc._id);
                              setEditingName(doc.name);
                            }}
                            className="edit-button"
                            title="Edit document name"
                          >
                            <FiEdit2 style={{ marginRight: '8px' }} /> Edit
                          </button>
                          <button 
                            onClick={() => handleDelete(doc._id)}
                            className="delete-button"
                            title="Delete document"
                          >
                            <FiTrash2 style={{ marginRight: '8px' }} /> Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentRecords;